import { Button, Col, Input, Label, Row } from 'reactstrap';

interface DocumentsChecklistProps {
  hireAgreementsList: any[];
  hiringAgreements: any[];
  taxDocs: any[];
  employmentEligibilityDocs: any[];
  otherDocs: any[];
  i9Option: string;
  lockedKeys?: string[];
  changeEmploymentEligibility: (e: any) => void;
  changeTaxDocs: (e: any) => void;
  changeOtherDocs: (e: any) => void;
  setHiringAgreements: (e: any) => void;
  setI9Option: (e: any) => void;
  onSelectAll?: () => void;
}

const DocumentsChecklist = ({
  hireAgreementsList,
  hiringAgreements,
  taxDocs,
  employmentEligibilityDocs,
  otherDocs,
  i9Option,
  lockedKeys = [],
  changeEmploymentEligibility,
  changeTaxDocs,
  changeOtherDocs,
  setHiringAgreements,
  setI9Option,
  onSelectAll,
}: DocumentsChecklistProps) => {
  const isLocked = (key: string) => lockedKeys.includes(key);
  const i9Locked = lockedKeys.some((key) => String(key).toUpperCase().startsWith('I9'));

  const hiringMain = hireAgreementsList.filter((a) => a.category !== 'MANAGEMENT');
  const hiringManagement = hireAgreementsList.filter((a) => a.category === 'MANAGEMENT');

  const renderAgreements = (items: any[]) =>
    items.map((agreement) => (
      <div className="form-check form-check-inline mr-3" key={agreement.id}>
        <Input
          className="form-check-input"
          type="checkbox"
          id={agreement.id}
          value={agreement.id}
          disabled={isLocked(agreement.id)}
          checked={hiringAgreements.includes(agreement.id)}
          onChange={(e) => {
            const isChecked = hiringAgreements.includes(e.target.value);
            const newHiringAgreements = [...hiringAgreements];

            if (isChecked) {
              setHiringAgreements(newHiringAgreements.filter((value) => value !== e.target.value));
            } else {
              newHiringAgreements.push(e.target.value);
              setHiringAgreements(newHiringAgreements);
            }
          }}
        />
        <Label className="form-check-label mr-2" htmlFor={agreement.id}>
          {agreement.name}
        </Label>
      </div>
    ));

  return (
    <>
      {onSelectAll ? (
        <Row className="d-flex aligm-items-center">
          <Col lg="3">
            <label className="form-control-label"></label>
          </Col>
          <Col lg="9">
            <Button className="btn-secondary" type="button" onClick={() => onSelectAll()} style={{ width: '150px' }}>
              Select All
            </Button>
          </Col>
        </Row>
      ) : null}
      <Row className="d-flex aligm-items-center mt-4">
        <Col lg="3">
          <label className="form-control-label">Employment Eligibility:</label>
        </Col>
        <Col lg="9">
          <div className="d-flex flex-column flex-wrap">
            {['English', 'Spanish'].map((lang: string) => (
              <div className="form-check form-check-inline align-items-start" key={`i9-${lang}`}>
                <Input
                  className="form-check-input mt-2"
                  name="i9-option"
                  type="radio"
                  id={`i9-${lang}`}
                  value={`I9_${lang.toUpperCase()}`}
                  disabled={i9Locked}
                  checked={`I9_${lang.toUpperCase()}` === i9Option}
                  onChange={() => setI9Option(`I9_${lang.toUpperCase()}`)}
                />
                <Label className="form-check-label mr-2" htmlFor={`i9-${lang}`}>
                  I-9 ({lang})
                </Label>
              </div>
            ))}

            <div className="form-check form-check-inline mt-2 align-items-start">
              <Input
                className="form-check-input mt-2"
                type="checkbox"
                id="request-proof-eligibility-documents"
                disabled={isLocked('TWO_DOCS_FOR_PROOF')}
                checked={!!employmentEligibilityDocs.find((e) => e === 'TWO_DOCS_FOR_PROOF')}
                onChange={() => changeEmploymentEligibility('TWO_DOCS_FOR_PROOF')}
              />
              <Label className="form-check-label mr-2" htmlFor="request-proof-eligibility-documents">
                Employment Eligibility Documents
              </Label>
            </div>
          </div>
        </Col>
      </Row>

      <Row className="d-flex aligm-items-center mt-4">
        <Col lg="3">
          <label className="form-control-label">Government Documents:</label>
        </Col>
        <Col lg="9">
          <div className="d-flex flex-column flex-wrap">
            <div className="form-check form-check-inline mt-2 align-items-start">
              <Input
                className="form-check-input mt-2"
                type="checkbox"
                id="tax-documents-w4"
                disabled={isLocked('W4')}
                checked={!!taxDocs.find((e) => e === 'W4')}
                onChange={() => changeTaxDocs('W4')}
              />
              <Label className="form-check-label mr-2" htmlFor="tax-documents-w4">
                W4 Federal Form
              </Label>
            </div>
            <div className="form-check form-check-inline mt-2 align-items-start">
              <Input
                className="form-check-input mt-2"
                type="checkbox"
                id={'IS54'}
                value={'IS54'}
                disabled={isLocked('IS54')}
                checked={otherDocs.includes('IS54')}
                onChange={() => changeOtherDocs('IS54')}
              />
              <Label className="form-check-label mr-2" htmlFor="tax-documents-is54">
                Notice and Acknowledgement of Pay Rate and Payday (NY Only)
              </Label>
            </div>
            <div className="form-check form-check-inline mt-2 align-items-start">
              <Input
                className="form-check-input mt-2"
                type="checkbox"
                id={'IT2104'}
                value={'IT2104'}
                disabled={isLocked('IT2104')}
                checked={otherDocs.includes('IT2104')}
                onChange={() => changeOtherDocs('IT2104')}
              />
              <Label className="form-check-label mr-2" htmlFor="tax-documents-it2104">
                NY Employee Withholding Allowance (NY Only)
              </Label>
            </div>
            <div className="form-check form-check-inline mt-2 align-items-start">
              <Input
                className="form-check-input mt-2"
                type="checkbox"
                id={'NC4'}
                value={'NC4'}
                disabled={isLocked('NC4')}
                checked={otherDocs.includes('NC4')}
                onChange={() => changeOtherDocs('NC4')}
              />
              <Label className="form-check-label mr-2" htmlFor="tax-documents-nc4">
                NC Withholding Allowance (NC Only)
              </Label>
            </div>
          </div>
        </Col>
      </Row>

      <Row className="d-flex aligm-items-center mt-4">
        <Col lg="3">
          <label className="form-control-label">Hiring Agreements:</label>
        </Col>
        <Col lg="9">
          <div className="d-flex flex-column flex-wrap">{renderAgreements(hiringMain)}</div>
        </Col>
      </Row>
      {hiringManagement.length > 0 && (
        <Row className="d-flex aligm-items-center mt-4">
          <Col lg="3">
            <label className="form-control-label">Management / Others:</label>
          </Col>
          <Col lg="9">
            <div className="d-flex flex-column flex-wrap">{renderAgreements(hiringManagement)}</div>
          </Col>
        </Row>
      )}
    </>
  );
};

export default DocumentsChecklist;
